import { ValidationStatus, FormValidationItem } from '@/entities/entity'

/**
 * Configuración de validación para un campo individual
 */
export interface FieldValidationConfig {
  field: string // nombre del campo en formData
  name: string // nombre para mostrar al usuario
  type: 'simple' | 'file' | 'link' // tipo de campo
  section: string // sección a la que pertenece
  order: number // orden dentro de la sección
  required?: boolean // si el campo es requerido
  dependsOn?: DependencyCondition // condición de dependencia
  customValidator?: (value: any, formData: any, context: ValidationContext) => ValidationStatus
}

/**
 * Condición de dependencia para campos condicionales
 */
export interface DependencyCondition {
  field: string // campo del que depende
  condition: (value: any, formData: any) => boolean // función que evalúa la condición
}

/**
 * Contexto de validación que incluye errores y estado del formulario
 */
export interface ValidationContext {
  refsErrors: Record<string, string> // errores de refs del formulario
  customData?: Record<string, any> // datos personalizados del formulario
  isLoading?: boolean // si el formulario está cargando
}

/**
 * Configuración completa para validación de formulario
 */
export interface FormValidationConfig {
  fields: FieldValidationConfig[]
  context: ValidationContext
}

/**
 * Función principal para validar un formulario completo
 * @param formData - datos del formulario
 * @param config - configuración de validación
 * @returns array de validaciones por campo (solo campos que deben validarse)
 */
export function validateForm (
  formData: Record<string, any>,
  config: FormValidationConfig
): FormValidationItem[] {
  const { fields, context } = config
  const validations: FormValidationItem[] = []

  for (const fieldConfig of fields) {
    // Solo validar campos que deben ser validados según sus dependencias
    if (shouldValidateField(formData, fieldConfig)) {
      const validation = validateField(formData, fieldConfig, context)
      validations.push(validation)
    }
    // Si shouldValidateField retorna false, el campo se omite completamente del resultado
  }

  return validations.sort((a, b) => a.order - b.order)
}

/**
 * Valida un campo individual
 * @param formData - datos del formulario
 * @param fieldConfig - configuración del campo
 * @param context - contexto de validación
 * @returns validación del campo
 */
export function validateField (
  formData: Record<string, any>,
  fieldConfig: FieldValidationConfig,
  context: ValidationContext
): FormValidationItem {
  const { field, name, type, section, order, required = false, customValidator } = fieldConfig

  // Si hay un validador personalizado, usarlo
  if (customValidator) {
    const customStatus = customValidator(formData[field], formData, context)
    return {
      value: customStatus,
      name,
      section,
      order,
      link: getFieldLink(formData[field], type),
    }
  }

  // Validación por tipo de campo
  const status = validateByType(formData[field], type, required, context.refsErrors[field])

  return {
    value: status,
    name,
    section,
    order,
    link: getFieldLink(formData[field], type),
  }
}

/**
 * Verifica si un campo tiene valor según su tipo
 */
function hasValue (value: any, type: 'simple' | 'file' | 'link'): boolean {
  switch (type) {
    case 'simple':
      return value !== null && value !== undefined && value !== ''
    case 'file':
      return Array.isArray(value) && value.length > 0
    case 'link':
      return value !== null && value !== undefined && value !== ''
    default:
      return false
  }
}

/**
 * Determina si un campo debe ser validado basado en sus dependencias
 * @param formData - datos del formulario
 * @param fieldConfig - configuración del campo
 * @returns true si debe validarse, false si no
 */
export function shouldValidateField (
  formData: Record<string, any>,
  fieldConfig: FieldValidationConfig
): boolean {
  if (!fieldConfig.dependsOn) {
    return true
  }

  const { field, condition } = fieldConfig.dependsOn
  const dependentValue = formData[field]

  return condition(dependentValue, formData)
}

/**
 * Valida un campo según su tipo
 * @param value - valor del campo
 * @param type - tipo de campo
 * @param required - si es requerido
 * @param errorMessage - mensaje de error del ref
 * @returns estado de validación
 */
export function validateByType (
  value: any,
  type: 'simple' | 'file' | 'link',
  required: boolean,
  errorMessage?: string
): ValidationStatus {
  // IMPORTANTE: Si hay error en el ref, siempre necesita corrección,
  // independientemente de si el campo es requerido u opcional
  if (errorMessage?.length) {
    return ValidationStatus.NEED_CORRECTION
  }
  switch (type) {
    case 'simple':
      return validateSimpleValue(value, required)

    case 'file':
      return validateFileValue(value, required)

    case 'link':
      return validateLinkValue(value, required)

    default:
      return ValidationStatus.PENDING
  }
}

/**
 * Valida un campo simple (texto, número, booleano, etc.)
 */
function validateSimpleValue (value: any, required: boolean): ValidationStatus {
  if (!required) {
    return value !== null && value !== undefined ? ValidationStatus.OK : ValidationStatus.OPTIONAL
  }

  // Para campos requeridos
  if (value === null || value === undefined) {
    return ValidationStatus.PENDING
  }

  // Para arrays
  if (Array.isArray(value)) {
    return value.length > 0 ? ValidationStatus.OK : ValidationStatus.PENDING
  }

  // Para strings
  if (typeof value === 'string') {
    return value.trim().length > 0 ? ValidationStatus.OK : ValidationStatus.PENDING
  }

  // Para booleanos, números y objetos
  return ValidationStatus.OK
}

/**
 * Valida un campo de archivo
 */
function validateFileValue (value: any, required: boolean): ValidationStatus {
  // Primero verificar si es un array válido
  if (!Array.isArray(value)) {
    return required ? ValidationStatus.PENDING : ValidationStatus.OPTIONAL
  }

  // Verificar si hay archivos con comentarios de error
  // IMPORTANTE: Si hay comentarios de error, siempre es NEED_CORRECTION,
  // independientemente de si el campo es requerido u opcional
  const hasErrorComments = value.some(file => file?.comment?.length)
  if (hasErrorComments) {
    return ValidationStatus.NEED_CORRECTION
  }

  // Si es requerido y no hay archivos
  if (required && value.length === 0) {
    return ValidationStatus.PENDING
  }

  // Si hay archivos sin errores
  if (value.length > 0) {
    return ValidationStatus.OK
  }

  // Si no es requerido y no hay archivos
  return ValidationStatus.OPTIONAL
}

/**
 * Valida un campo de enlace/URL
 */
function validateLinkValue (value: any, required: boolean): ValidationStatus {
  if (!required && (!value || value === '')) {
    return ValidationStatus.OPTIONAL
  }

  if (required && (!value || value === '')) {
    return ValidationStatus.PENDING
  }

  // Validación básica de URL
  try {
    // Crear URL para validar formato, asignar a variable para evitar linter warning
    return ValidationStatus.OK
  } catch {
    // Si no es una URL válida, verificar si es un enlace relativo válido
    if (typeof value === 'string' && value.length > 0) {
      return ValidationStatus.OK
    }
    return ValidationStatus.NEED_CORRECTION
  }
}

/**
 * Obtiene el enlace de un campo para mostrar en el stepper
 */
function getFieldLink (value: any, type: string): string | string[] | null {
  if (type === 'link' && value) {
    return value
  }

  if (type === 'file' && Array.isArray(value)) {
    const links = value.map(file => file?.url || file?.sourceLink).filter(Boolean)
    return links.length > 0 ? (links.length === 1 ? links[0] : links) : null
  }

  return null
}

/**
 * Validadores de dependencia predefinidos para casos comunes
 */
export const DependencyValidators = {
  /**
   * El campo depende de que otro campo sea true
   */
  requiresTrue: (field: string): DependencyCondition => ({
    field,
    condition: value => value === true,
  }),

  /**
   * El campo depende de que otro campo tenga un valor específico
   */
  requiresValue: (field: string, expectedValue: any): DependencyCondition => ({
    field,
    condition: value => value === expectedValue,
  }),

  /**
   * El campo depende de que otro campo tenga algún valor
   */
  requiresAnyValue: (field: string): DependencyCondition => ({
    field,
    condition: value => value !== null && value !== undefined && value !== '',
  }),

  /**
   * El campo depende de que otro campo tenga un ID válido
   */
  requiresValidId: (field: string): DependencyCondition => ({
    field,
    condition: value => value?.id !== null && value?.id !== undefined,
  }),

  /**
   * El campo depende de múltiples condiciones (AND)
   */
  requiresAll: (conditions: DependencyCondition[]): DependencyCondition => ({
    field: 'multiple',
    condition: (_, formData) => conditions.every(cond => cond.condition(formData[cond.field], formData)),
  }),
}
